import { ClipboardCheck, Users, ShieldCheck, MessageSquareText, PhoneCall, HelpCircle, History, Globe, LayoutGrid, Bot, Layers, Workflow, Zap, X, Check } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/fx";

const BENTO = [
  { t: "Plan-first, always", d: "A brief, a system map and a build plan you approve before a single file is written.", I: ClipboardCheck, span: "md:col-span-2" },
  { t: "An agent team", d: "Product, UX, Architect, Database, Frontend, Backend, QA, Security, Deploy.", I: Users },
  { t: "Real tests — never fake “done”", d: "Every check runs for real. Failures go to the Repair Agent, then get re-proven.", I: ShieldCheck },
  { t: "App Copilot", d: "Ask for a change in plain words; FORGE re-plans the diff and rebuilds safely.", I: MessageSquareText },
  { t: "FORGE Connect", d: "SMS, voice, video and email for the apps you ship.", I: PhoneCall, span: "md:col-span-2" },
  { t: "Ask-AI explain everywhere", d: "Click any rule, node or check to get a plain-English explanation.", I: HelpCircle },
  { t: "Versions & undo", d: "Every successful build is a snapshot. Restore in one click.", I: History, span: "md:col-span-2" },
];

export const Bento = () => (
  <section data-testid="bento-section" className="mx-auto max-w-7xl px-6 py-16 md:py-20">
    <div className="grid gap-4 md:grid-cols-3">
      {BENTO.map((b, i) => (
        <Reveal key={b.t} delay={i * 0.05} className={b.span || ""}>
          <motion.div whileHover={{ y: -4 }} className="glass group relative h-full overflow-hidden rounded-3xl p-7">
            <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#7a5cff]/20 blur-3xl transition-opacity duration-500 group-hover:opacity-100 md:opacity-40" />
            <b.I className="h-7 w-7 text-[#22d3ee]" />
            <div className="font-display mt-5 text-xl font-semibold text-white">{b.t}</div>
            <p className="mt-2 text-sm text-white/60">{b.d}</p>
          </motion.div>
        </Reveal>
      ))}
    </div>
  </section>
);

const TILES = [["Websites", Globe], ["Web apps", LayoutGrid], ["AI agents", Bot], ["SaaS", Layers], ["Automations", Zap], ["Workflows", Workflow]];
const LANES = [
  ["“A booking app for a barbershop that never double-books a barber.”", "Booking flow, barber schedules, owner dashboard — overlap rule enforced on the server."],
  ["“A landing page for a coffee roastery with a newsletter signup.”", "Story, roasts and subscriptions — unique-email rule and a live signup list."],
  ["“A support site for a dental clinic with an AI receptionist.”", "Services, hours, FAQs and a chat agent grounded in the clinic’s own data."],
];

export const WhatItBuilds = () => (
  <section id="builds" data-testid="what-it-builds-section" className="mx-auto max-w-7xl px-6 py-16 md:py-20">
    <Reveal><span className="label-mono text-[#ff5c7a]">What it builds</span></Reveal>
    <Reveal delay={0.05}><h2 className="font-display mt-3 text-3xl font-bold text-white md:text-5xl">If you can describe it, FORGE can ship it.</h2></Reveal>
    <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-6">
      {TILES.map(([t, I], i) => (
        <Reveal key={t} delay={i * 0.05}>
          <motion.div whileHover={{ scale: 1.04, rotate: -1 }} className="glass flex flex-col items-center gap-3 rounded-2xl px-4 py-7 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-[#7a5cff]/40 to-[#22d3ee]/20"><I className="h-6 w-6 text-white" /></span>
            <span className="font-display text-sm font-semibold text-white">{t}</span>
          </motion.div>
        </Reveal>
      ))}
    </div>
    <div className="mt-12 space-y-3">
      {LANES.map(([a, b], i) => (
        <Reveal key={i} delay={i * 0.08}>
          <div className="grid items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:grid-cols-[1fr_auto_1fr]">
            <div><div className="label-mono text-white/40">You type</div><div className="mt-1 font-mono text-sm text-white/85">{a}</div></div>
            <div className="hidden h-px w-24 bg-gradient-to-r from-[#22d3ee] to-[#ff5c7a] md:block" />
            <div><div className="label-mono text-white/40">You get</div><div className="mt-1 text-sm text-white/75">{b}</div></div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const ROWS = [
  ["Starts by generating code", "Starts with a brief, system map and plan you approve"],
  ["Says “done” when the code compiles", "Says READY only after real checks pass"],
  ["Business rules live in prompts", "Business rules enforced server-side and probed by tests"],
  ["Errors are yours to find", "Failures trigger an automatic repair loop, then re-tests"],
  ["One generic model", "Specialist agents, your choice of Claude Sonnet 5.5 or GPT 5.5"],
  ["Green checkmarks you can't inspect", "Reality Engine evidence: screenshots, requests, PASSED / FAILED / UNVERIFIED"],
  ["Edits go straight to your live app", "Impact → draft → tests → publish or discard, with one-click rollback"],
  ["Code is the only source of truth", "An Application Constitution: a versioned living twin of rules, data and screens"],
  ["Messaging is an add-on you wire yourself", "SMS, MMS, voice, video, email + automations built in, with consent and STOP"],
  ["Surprise bills", "Cost estimate before every build, actual cost after"],
];

export const Compare = () => (
  <section data-testid="compare-section" className="mx-auto max-w-5xl px-6 py-16 md:py-20">
    <Reveal><h2 className="font-display text-center text-3xl font-bold text-white md:text-5xl">Typical AI builders vs <span className="text-fire">FORGE</span></h2></Reveal>
    <Reveal delay={0.1}>
      <div className="glass mt-12 overflow-hidden rounded-3xl">
        <div className="grid grid-cols-2 border-b border-white/10 px-6 py-4">
          <div className="label-mono text-white/45">Typical AI builders</div><div className="label-mono text-[#22d3ee]">FORGE</div>
        </div>
        {ROWS.map(([a, b]) => (
          <div key={a} className="grid grid-cols-2 gap-4 border-b border-white/5 px-6 py-4 text-sm last:border-0">
            <div className="flex gap-2 text-white/50"><X className="mt-0.5 h-4 w-4 shrink-0 text-[#ff5c7a]" />{a}</div>
            <div className="flex gap-2 text-white/85"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#6fd6b0]" />{b}</div>
          </div>
        ))}
      </div>
    </Reveal>
  </section>
);
